/**
 * Component: EvvntHtmlBlock
 *
 * Description:
 * This component renders raw HTML content passed via block attributes.
 * It specifically supports EVVNT widget embed code by ensuring any embedded
 * scripts are re-executed properly in a client-rendered Next.js environment.
 *
 * - On mount: Executes embedded scripts using DOM manipulation.
 * - On route change: Re-initializes EVVNT widget when navigating via SPA routing.
 *
 * Dependencies:
 * - Next.js (useRouter)
 * - React
 * - MUI (Box component for layout/styling)
 */

import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';

const EvvntHtmlBlock: React.FC<any> = ({ block }) => {
  const { content } = block.attributes;
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  /**
   * Executes embedded <script> tags within the HTML block
   * by replacing them with new script elements to trigger execution.
   */
  const runScripts = () => {
    if (!containerRef.current) return;

    const scriptElements = containerRef.current.querySelectorAll('script');

    scriptElements.forEach((oldScript) => {
      const newScript = document.createElement('script');

      // Copy all attributes from old <script> to new one
      for (let i = 0; i < oldScript.attributes.length; i += 1) {
        const attr = oldScript.attributes[i];
        newScript.setAttribute(attr.name, attr.value);
      }

      // Reinsert inline or external scripts
      if (oldScript.src) {
        newScript.onload = () => {
          // Run EVVNT init function if present
          if (typeof (window as any).evvntDiscoveryInit === 'function') {
            (window as any).evvntDiscoveryInit();
          }
        };
      } else {
        newScript.text = oldScript.textContent || '';
      }

      // Replace old script with new one to trigger execution
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });
  };

  // Run scripts when content is initially rendered
  useEffect(() => {
    runScripts();
  }, [content]);

  // Re-run EVVNT init on SPA route changes (Next.js)
  useEffect(() => {
    const handleRouteChange = () => {
      setTimeout(() => {
        if (
          typeof window !== 'undefined' &&
          typeof (window as any).evvntDiscoveryInit === 'function' &&
          document.querySelector('#evvnt-calendar')
        ) {
          (window as any).evvntDiscoveryInit();
        }
      }, 100); // Delay ensures DOM has updated
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  return (
    <Box
      id="evvnt-html-block"
      ref={containerRef}
      sx={{ my: 4 }}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

export default EvvntHtmlBlock;
